import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { RootState } from "../../app/store";
import { StartSyncResponseType, SyncJobType } from "../../types/types";
import { API_URL } from "../../config";

export type StartSyncArgs = {
  /** Compute and report the whole diff without writing anything. */
  dryRun?: boolean;
  /** Cap on how much of Alegra the run reads — the incremental rollout's first pass. */
  maxItems?: number;
};

export const alegraSyncApi = createApi({
  reducerPath: "alegraSync",
  baseQuery: fetchBaseQuery({
    baseUrl: API_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["SyncJob"],
  endpoints: (builder) => ({
    // Answers 202 with a jobId, not the result: the run takes minutes in a second, asynchronous
    // Lambda invocation. 409 means another sync already holds the single-flight lock.
    startSync: builder.mutation<StartSyncResponseType, StartSyncArgs | void>({
      query: (args) => ({
        url: "alegra-sync",
        method: "POST",
        body: args ?? {},
      }),
    }),

    // Polled while a job runs. Deliberately not tagged as invalidated by `startSync` — the caller
    // drives the polling interval and stops it on a terminal status, which a tag cannot express.
    getSyncJob: builder.query<SyncJobType, string>({
      query: (jobId) => ({
        url: `alegra-sync/${jobId}`,
        method: "GET",
      }),
      providesTags: (_result, _error, jobId) => [{ type: "SyncJob", id: jobId }],
    }),
  }),
});

export const { useStartSyncMutation, useGetSyncJobQuery } = alegraSyncApi;

export default alegraSyncApi;
